import React, { useCallback, useEffect, useRef, useState } from "react";
import { LogIn, LogOut, Settings, User } from "@/icons/lucide-compat";
import { cn } from "@openreel/ui";
import { useAuth } from "../services/auth";
import type { AuthUser } from "../services/auth";
import { useSettingsStore } from "../stores/settings-store";

// Kept in sync with OnboardingGate's ANON_KEY. The gate samples this flag once
// on mount, so sending an anonymous visitor back to the sign-in screen means
// clearing the flag and reloading, not a React state change.
const ANON_KEY = "flshy.continue-anonymously";

function initialsFor(user: AuthUser): string {
  const fromName = [user.firstName, user.lastName]
    .filter((part): part is string => typeof part === "string" && part.length > 0)
    .map((part) => part[0])
    .join("");
  if (fromName.length > 0) return fromName.toUpperCase().slice(0, 2);
  return user.email.slice(0, 1).toUpperCase();
}

function displayNameFor(user: AuthUser): string {
  const joined = [user.firstName, user.lastName]
    .filter((part): part is string => typeof part === "string" && part.length > 0)
    .join(" ")
    .trim();
  return user.name?.trim() || joined || user.email;
}

export interface AccountMenuProps {
  /** Which edge the dropdown aligns to. Defaults to the right edge. */
  align?: "start" | "end";
  /**
   * Which way the dropdown opens. "below" is the header default; a bottom
   * pinned trigger (the dashboard sidebar) needs "above" so the menu is not
   * clipped off the bottom of the viewport.
   */
  placement?: "below" | "above";
  /**
   * "surface" dresses the trigger for the fixed near-black canvas the sign-in,
   * onboarding and dashboard share, where controls are borderless and the
   * themed editor tokens do not apply.
   */
  variant?: "chrome" | "surface";
  className?: string;
}

/**
 * Avatar button plus dropdown for the signed-in user, wired to the real auth
 * service. Renders a distinct anonymous state (someone who chose "Continue
 * without an account") that offers a way in without ever fabricating an
 * identity: no name, no email, no avatar image.
 */
export function AccountMenu({ align = "end", placement = "below", variant = "chrome", className }: AccountMenuProps): React.JSX.Element {
  const { user, signOut } = useAuth();
  const openSettings = useSettingsStore((s) => s.openSettings);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleSignIn = useCallback(() => {
    setOpen(false);
    try {
      window.localStorage.removeItem(ANON_KEY);
    } catch {
      // A blocked storage API is not a reason to trap the visitor here.
    }
    // The gate reads the anonymous flag once on mount, so a reload is what
    // returns the visitor to it.
    window.location.reload();
  }, []);

  const handleSignOut = useCallback(() => {
    setOpen(false);
    // signOut settles the store into the anonymous state, which the gate that
    // wraps the app re-renders to as the sign-in screen. It never throws.
    void signOut();
  }, [signOut]);

  const handleOpenProfile = useCallback(() => {
    setOpen(false);
    openSettings("profile");
  }, [openSettings]);

  const handleOpenSettings = useCallback(() => {
    setOpen(false);
    openSettings("general");
  }, [openSettings]);

  const triggerLabel = user
    ? `Account: ${displayNameFor(user)}`
    : "Account";

  const itemClass =
    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-fg-2 transition-colors hover:bg-fg/8 hover:text-fg";
  // Sign out reads as quietly destructive: no red at rest, a soft red wash on hover.
  const destructiveItemClass =
    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-fg-2 transition-colors hover:bg-red-500/12 hover:text-red-400";

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg transition-colors ${
          variant === "surface"
            ? "border-0 bg-white/[0.06] text-[13px] font-medium text-[#a8a8a8] hover:bg-white/[0.12] hover:text-white"
            : "border border-border bg-bg-2 text-[12px] font-normal text-fg-2 hover:bg-bg-3 hover:text-fg"
        }`}
      >
        {user && user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt=""
            width={32}
            height={32}
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : user ? (
          <span aria-hidden>{initialsFor(user)}</span>
        ) : (
          <User size={16} aria-hidden />
        )}
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            "glass absolute z-50 w-64 rounded-2xl p-1.5",
            placement === "above" ? "bottom-full mb-2" : "mt-2",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          {user ? (
            <div className="flex items-center gap-3 px-2.5 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-fg/10 text-[12px] font-normal text-fg">
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    width={36}
                    height={36}
                    className="h-full w-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span aria-hidden>{initialsFor(user)}</span>
                )}
              </span>
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium text-fg">
                  {displayNameFor(user)}
                </div>
                <div className="truncate text-[12px] text-fg-3">{user.email}</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-2.5 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-fg/10 text-fg-3">
                <User size={17} aria-hidden />
              </span>
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-fg">Not signed in</div>
                <div className="text-[12px] text-fg-3">
                  Sign in to sync projects and cloud AI.
                </div>
              </div>
            </div>
          )}

          {/* Low-alpha divider stands in for the old hard border between the
              header block and the actions. */}
          <div className="mx-1 my-1 h-px bg-fg/10" />

          {user ? (
            <>
              <button type="button" role="menuitem" onClick={handleOpenProfile} className={itemClass}>
                <User size={15} aria-hidden />
                Profile
              </button>
              <button type="button" role="menuitem" onClick={handleOpenSettings} className={itemClass}>
                <Settings size={15} aria-hidden />
                Settings
              </button>
              <button type="button" role="menuitem" onClick={handleSignOut} className={destructiveItemClass}>
                <LogOut size={15} aria-hidden />
                Sign out
              </button>
            </>
          ) : (
            <>
              <button type="button" role="menuitem" onClick={handleSignIn} className={itemClass}>
                <LogIn size={15} aria-hidden />
                Sign in
              </button>
              <button type="button" role="menuitem" onClick={handleOpenSettings} className={itemClass}>
                <Settings size={15} aria-hidden />
                Settings
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default AccountMenu;
